import React, { useEffect, useState } from 'react';
import { Button } from 'antd';

import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  getAssignmentImportResult,
  getAssignmentImportsInfo,
  getAssignmentUKGImport,
} from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  AssignmentToKronosInfoExistViewModel,
  KronosImportTypeEnum,
  KronosInfoStatusEnum,
} from 'api/adminApiClient';
import { downloadFileByObjectURL } from 'utils/fileUtils';

import styles from '../AssignmentDetailsPage.module.less';

type Props = {
  assignmentId?: string;
  ukgStatus?: KronosInfoStatusEnum;
};

export default function DownloadUKGFile(props: Props) {
  const { assignmentId, ukgStatus } = props;
  const [isImportsInfoLoaded, setIsImportsInfoLoaded] = useState(false);
  const [importsInfo, setImportsInfo] =
    useState<AssignmentToKronosInfoExistViewModel>();
  const dispatch = useAppDispatch();
  const isAwaiting = ukgStatus === KronosInfoStatusEnum.AwaitingStatus;

  const hasImport =
    importsInfo?.import1Exist ||
    importsInfo?.import2Exist ||
    importsInfo?.import3Exist ||
    importsInfo?.import4Exist ||
    importsInfo?.multiRatesExist ||
    importsInfo?.terminationDetailsExist ||
    importsInfo?.terminationExist;

  const isHidden = !isImportsInfoLoaded || !hasImport;

  const loadAssignmentImportsInfo = async () => {
    if (!assignmentId) return;

    const result = await dispatch(getAssignmentImportsInfo(assignmentId));

    if (getAssignmentImportsInfo.fulfilled.match(result)) {
      setImportsInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsImportsInfoLoaded(true);
  };

  const loadAssignmentUKGImport = async (type: KronosImportTypeEnum) => {
    if (!assignmentId) return;

    const result = await dispatch(
      getAssignmentUKGImport({ assignmentId, type }),
    );

    if (getAssignmentUKGImport.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadAssignmentImportResult = async (type: KronosImportTypeEnum) => {
    if (!assignmentId) return;

    const result = await dispatch(
      getAssignmentImportResult({ assignmentId, type }),
    );

    if (getAssignmentImportResult.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadUKGFile = async () => {
    if (importsInfo?.import1Exist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.I1CostCenter),
        loadAssignmentImportResult(KronosImportTypeEnum.I1CostCenter),
      ]);
    }
    if (importsInfo?.import2Exist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.I2ApproversAgent),
        loadAssignmentImportResult(KronosImportTypeEnum.I2ApproversAgent),
      ]);
    }
    if (importsInfo?.import3Exist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.I3Employee),
        loadAssignmentImportResult(KronosImportTypeEnum.I3Employee),
      ]);
    }
    if (importsInfo?.import4Exist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.I4UnemploymentState),
        loadAssignmentImportResult(KronosImportTypeEnum.I4UnemploymentState),
      ]);
    }
    if (importsInfo?.multiRatesExist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.MultiRates),
        loadAssignmentImportResult(KronosImportTypeEnum.MultiRates),
      ]);
    }
    if (importsInfo?.terminationDetailsExist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.TerminationDetails),
        loadAssignmentImportResult(KronosImportTypeEnum.TerminationDetails),
      ]);
    }
    if (importsInfo?.terminationExist) {
      await Promise.all([
        loadAssignmentUKGImport(KronosImportTypeEnum.Termination),
        loadAssignmentImportResult(KronosImportTypeEnum.Termination),
      ]);
    }
  };

  useEffect(() => {
    loadAssignmentImportsInfo();
    // eslint-disable-next-line
  }, [assignmentId]);

  if (isHidden) return null;

  return (
    <div>
      <Button
        disabled={isAwaiting}
        className={styles.downloadBtn}
        onClick={handleDownloadUKGFile}
        icon={<span className="icon icon-ukg-file" />}
      />
    </div>
  );
}
